import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import { type CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import { formatDiagnosisCodeText } from "@/modules/patients/components/diagnosis-code"
import { useChGeneratePsychotherapyDocumentMutation as useChGeneratePsychotherapyDocumentMutationType } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/useChGeneratePsychotherapyDocumentMutation.graphql"
import { getDiagnosisCodeByDiagnosisCode } from "@/modules/patients/detail/therapy-prescription/ch/services/getDiagnosisCodeByDiagnosisCode"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const mutation = graphql`
  mutation useChGeneratePsychotherapyDocumentMutation($input: Ch_generatePsychotherapyPrescriptionInput!) {
    ch_generatePsychotherapyPrescription(input: $input) {
      ch_PsychotherapyPrescription {
        id
        fileId
        ...chPsychologicalPsychoTherapyFragment
      }
      errors {
        ... on Error {
          message
        }
      }
    }
  }
`

type Config = Omit<UseMutationConfig<useChGeneratePsychotherapyDocumentMutationType>, "variables"> & {
  id: string
  diagnosisCode: string
}

const useChGeneratePsychotherapyDocumentMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<useChGeneratePsychotherapyDocumentMutationType>(
    mutation,
    params,
  )
  const patientById = usePatientParam()

  const commitHandler = useCallback(
    async (config: Config) => {
      const { id, diagnosisCode } = config

      const { code, description } = await getDiagnosisCodeByDiagnosisCode(diagnosisCode)
      const formattedDiagnosisCode = formatDiagnosisCodeText(code, description)

      commit({
        variables: {
          input: {
            id,
            patientId: patientById,
            diagnosisCode: formattedDiagnosisCode,
          },
        },
        ...config,
      })
    },
    [commit, patientById],
  )

  return [commitHandler, isFlight] as const
}

export { useChGeneratePsychotherapyDocumentMutation }
